import React, { useState, useEffect } from 'react';

import moment from 'moment';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';

import Tooltip from '../../../../Tooltip';
import BillComId from '../../../FormContainer/BillComId';
import Select from '../../../../Form/Select';
import CurrencyInput from '../../../../Form/CurrencyInput';
import TextInput from '../../../../Form/TextInput';
import Table from '../../../../Table';
import Datepicker from '../../../../Form/Datepicker';
import FormContainer from '../../../FormContainer';
import Alert from '../../../../Alert';
import DeleteButton from './DeleteButton';
import ScheduledPayment from './ScheduledPayment';
import EditableInput from './EditableInput';
import Buttons from './Buttons';
import PaymentInfoLink from './PaymentInfoLink';

import DatePaid from './DatePaid';

import useLineItemForm from '../../../../../forms/loan/lineItem';
import LineItemService from '../../../../../services/LineItemService';
import InvoiceService from '../../../../../services/InvoiceService';
import DocumentService from '../../../../../services/DocumentService';
import { useUiContext } from '../../../../../context/UiContext';
import { formatCurrency } from '../../../../../helpers/currencyMask';

const createPaymentLineItemsList = (lineItems, loanId) => {
  return (
    <ul>
      {lineItems.map((item) => (
        <li key={item.id}>
          <PaymentInfoLink paymentLineItem={item} loanId={loanId} />
        </li>
      ))}
    </ul>
  );
};

const invoiceStatuses = [
  { label: 'Pending', value: 'pending' },
  { label: 'Open', value: 'open' },
  { label: 'Past Due', value: 'past due' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Due', value: 'due' },
];

function createPaidCellTooltip(row) {
  if (row?.description === 'Total') {
    return '';
  }

  const items = [];

  row._payment_line_items?.forEach((item) => {
    items.push(
      `$${formatCurrency(item.amount)} | ${item._payment?.source} | ${moment(
        item._payment?.date
      ).format('M/D/YY')}`
    );
  });

  return (
    <ul>
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>
          <Link
            to={`/loans/${row._payment_line_items?.[index]?._payment?.loan_id}/payments/${row._payment_line_items?.[index]?._payment?.id}?tab=Payment`}
            className="text-indigo-500 underline"
          >
            {item}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function isLineItemEditingDisabled(row, readOnly, invoiceStatus) {
  return (
    row.description === 'Total' ||
    readOnly ||
    row.locked ||
    invoiceStatus === 'paid'
  );
}

function setActionType(row) {
  // Never show delete button for paid line items
  if (row.status === 'paid' || row.status === 'partial') {
    return 'button';
  }

  // Show delete button only for unlocked, non-total line items that are not paid
  if (!row.locked && row.description !== 'Total') {
    return 'delete';
  }

  return '';
}

function handleActionButtonPress({
  row,
  showDialogModal,
  showToast,
  refetch,
  setIsStatusUpdateDisabled,
}) {
  const isDeleteButton = setActionType(row) === 'delete';

  return showDialogModal({
    type: 'danger',
    title: isDeleteButton
      ? 'Are you sure want to delete this line item?'
      : 'Are you sure you want to reverse this line item?',
    confirmButtonLabel: isDeleteButton ? undefined : 'Yes, reverse it',
    cancelButtonLabel: isDeleteButton ? undefined : 'No, cancel',
    onConfirm: async () => {
      try {
        setIsStatusUpdateDisabled(true);
        if (isDeleteButton) {
          await LineItemService.deleteLineItem(row.id);
        } else {
          await LineItemService.reversePayment(row.id);
        }

        showToast({
          text: isDeleteButton
            ? 'Line item deleted successfully'
            : 'Payment reversed successfully',
          type: 'success',
        });
        await refetch();
      } catch (error) {
        // Error is handled in the toast below
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      } finally {
        setIsStatusUpdateDisabled(false);
      }
    },
  });
}

const downloadStatement = async (statement, showToast) => {
  try {
    const documentWithSignedUrl = await DocumentService.getDocument(
      statement.document_id
    );

    const url = documentWithSignedUrl.signed_url;
    const a = document.createElement('a');
    a.href = url;
    a.download = documentWithSignedUrl.private_document.name;
    a.target = '_blank';
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  } catch (error) {
    // Error is handled in the toast below
    showToast({
      text: error?.response?.data?.message || 'Something went wrong',
      type: 'error',
    });
  }
};

export default function Invoice({ readOnly, loanId, loan }) {
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [scheduledPaymentsWithKeys, setScheduledPaymentsWithKeys] = useState(
    []
  );
  const [isStatusUpdateDisabled, setIsStatusUpdateDisabled] = useState(false);
  const [editinRows, setEditingRows] = useState([]);
  const [lineItemValues, setLineItemValues] = useState({});

  const { showToast, showDialogModal } = useUiContext();

  const { invoiceId } = useParams();

  const {
    data: invoice,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => InvoiceService.getInvoice(invoiceId),
    enabled: !!invoiceId,
  });

  // Save line item values on invoice change so we can change them later
  const createLineItemsValues = (invc) => {
    const lineItems = invc?._line_items;
    const values = {};
    lineItems.forEach((item) => {
      values[item.id] = {
        description: item.description,
        amount: item.amount,
      };
    });

    return values;
  };

  useEffect(() => {
    if (invoice) {
      const values = createLineItemsValues(invoice);
      setLineItemValues(values);
    }
  }, [invoice]);

  const resetLineItemValues = () => {
    const values = createLineItemsValues(invoice);
    setLineItemValues(values);
  };

  useEffect(() => {
    if (invoice) {
      setInvoiceStatus(invoice.status);
    }
  }, [invoice]);

  const isPaid = invoice?.status === 'paid';
  const isAllowedToChangeStatus = [
    'open',
    'due',
    'past due',
    'scheduled',
    'pending',
  ].includes(invoice?.status);
  const isPTypeInvoide = invoice?.type === 'P';

  useEffect(() => {
    if (invoice) {
      const payments = invoice.scheduled_payments.map((payment, index) => ({
        ...payment,
        key: index,
      }));

      setScheduledPaymentsWithKeys(payments);
    }
  }, [invoice, isPaid]);

  const addEmptyScheduledPayment = () => {
    setScheduledPaymentsWithKeys((prevState) => [
      ...prevState,
      {
        key: prevState.length,
        amount: '',
        date: '',
        isNew: true,
      },
    ]);
  };

  const removeEmptyScheduledPayment = (key) => {
    setScheduledPaymentsWithKeys((prevState) =>
      prevState.filter((payment) => payment.key !== key)
    );
  };

  const { mutate: updateInvoice, isPending } = useMutation({
    mutationFn: ({ date, status, scheduledPayments }) => {
      return InvoiceService.updateInvoice({
        data: {
          ...invoice,
          date,
          type: 'P',
          status,
          scheduled_payments: scheduledPayments,
        },
        invoiceId,
      });
    },
    onSuccess: () => {
      refetch();
      showToast({
        text: 'Invoice updated',
        type: 'success',
      });
    },
    onError: (err) => {
      // Error is handled in the toast below
      showToast({
        text: err?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const createOrupdateScheduledPayment = (formValues) => {
    let updatedPayments = [];

    if (formValues.isNew) {
      updatedPayments = [
        ...scheduledPaymentsWithKeys.slice(0, -1),
        { ...formValues, isNew: false },
      ];

      updatedPayments = updatedPayments.filter((p) => !p.isNew);
    } else {
      updatedPayments = scheduledPaymentsWithKeys
        .filter((p) => !p.isNew)
        .map((p) => (p.key === formValues.key ? formValues : { ...p }));
    }

    updateInvoice({
      date: invoiceDate,
      status: invoiceStatus,
      scheduledPayments: updatedPayments,
    });
  };

  const deleteScheduledPayment = (key) => {
    const updatedPayments = scheduledPaymentsWithKeys.filter(
      (payment) => payment.key !== key && !payment.isNew
    );

    updateInvoice({
      date: invoiceDate,
      status: invoiceStatus,
      scheduledPayments: updatedPayments,
    });
  };

  const { mutate: createLineItem } = useMutation({
    mutationFn: (formValues) => {
      return LineItemService.createLineItem({
        ...formValues,
        invoice_id: invoiceId,
        locked: false,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Line item created',
        type: 'success',
      });

      refetch();
    },
    onError: () => {
      // Error is handled in the toast below
      showToast({
        text: 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updateLineItem } = useMutation({
    mutationFn: (formValues) => {
      return LineItemService.updateLineItem(formValues.id, formValues);
    },
    onSuccess: () => {
      showToast({
        text: 'Line item updated',
        type: 'success',
      });

      refetch();
    },
    onError: () => {
      // Error is handled in the toast below
      showToast({
        text: 'Something went wrong',
        type: 'error',
      });
      resetLineItemValues();
    },
  });

  // Update invoice on invoiceStatus change
  useEffect(() => {
    if (isStatusUpdateDisabled) {
      return;
    }

    if (
      invoiceStatus &&
      invoiceStatus !== invoice?.status &&
      // We should not change paid inovice status
      invoice?.status !== 'paid'
    ) {
      updateInvoice({ date: invoiceDate, status: invoiceStatus });
    }
  }, [
    invoice?.status,
    invoiceDate,
    invoiceStatus,
    updateInvoice,
    isStatusUpdateDisabled,
  ]);

  useEffect(() => {
    if (invoice) {
      setInvoiceDate(invoice.date);
    }
  }, [invoice]);

  // Update invoice on date change
  useEffect(() => {
    if (invoiceDate && invoiceDate !== invoice?.date) {
      updateInvoice({ date: invoiceDate, status: invoiceStatus });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate]);

  const cols = [
    {
      title: 'Description',
      keys: ['description'],
      type: 'component',
      component: EditableInput,
      props: (row) => ({
        value: lineItemValues[row.id]?.description,
        onChange: (value) => {
          setLineItemValues((prevState) => ({
            ...prevState,
            [row.id]: { ...prevState[row.id], description: value },
          }));
        },
        type: 'text',
        onClick: () => {
          if (isLineItemEditingDisabled(row, readOnly, invoice?.status)) {
            return;
          }

          setEditingRows((prevState) => {
            if (prevState.includes(row.id)) {
              return prevState;
            }
            return [...prevState, row.id];
          });
        },
        isEdit: editinRows.includes(row.id),
      }),
      tooltip: (row) => {
        return row._account?.name;
      },
    },
    {
      title: 'Amount',
      keys: ['amount'],
      type: readOnly ? 'currency' : 'component',
      component: EditableInput,
      props: (row) => {
        return {
          value:
            row.description === 'Total'
              ? row.amount
              : lineItemValues[row.id]?.amount,
          onChange: (value) => {
            setLineItemValues((prevState) => ({
              ...prevState,
              [row.id]: { ...prevState[row.id], amount: value },
            }));
          },
          type: 'currency',
          onClick: () => {
            if (isLineItemEditingDisabled(row, readOnly, invoice?.status)) {
              return;
            }

            setEditingRows((prevState) => {
              if (prevState.includes(row.id)) {
                return prevState;
              }
              return [...prevState, row.id];
            });
          },
          isEdit: editinRows.includes(row.id),
        };
      },
    },
    {
      title: '',
      keys: ['doesntmatter'],
      type: 'component',
      component: Buttons,
      isHidden: () => readOnly,
      props: (row) => ({
        isVisible: editinRows.includes(row.id),
        onCancelClick: () => {
          setEditingRows((prevState) =>
            prevState.filter((id) => id !== row.id)
          );
          resetLineItemValues();
        },
        onSaveClick: () => {
          updateLineItem({
            ...row,
            description: lineItemValues[row.id].description,
            amount: `${lineItemValues[row.id].amount}`.replace(/,/g, ''),
          });
          setEditingRows((prevState) =>
            prevState.filter((id) => id !== row.id)
          );
        },
      }),
    },
    {
      title: 'Paid',
      keys: ['_paid.total'],
      type: 'currency',
      tooltip: createPaidCellTooltip,
    },
    {
      title: 'Status',
      keys: ['status'],

      tooltip: (row) => {
        const paymentLineItems = row?._payment_line_items;

        if (
          row.status !== 'paid' ||
          !paymentLineItems?.length ||
          paymentLineItems.length === 1
        ) {
          return null;
        }

        return createPaymentLineItemsList(paymentLineItems, loanId);
      },
      link: (row) => {
        const paymentLineItems = row?._payment_line_items;

        if (
          !paymentLineItems ||
          !paymentLineItems.length ||
          paymentLineItems.length > 1 ||
          row.status !== 'paid'
        ) {
          return null;
        }

        if (paymentLineItems.length === 1) {
          return `/loans/${loanId}/payments/${paymentLineItems[0]._payment?.id}?tab=Payment`;
        }

        return null;
      },
    },
    {
      title: 'Action',
      type: (row) => setActionType(row),
      label: () => 'Reverse Payment',
      isHidden: () => readOnly,
      onClick: (row) =>
        handleActionButtonPress({
          row,
          showDialogModal,
          showToast,
          refetch,
          readOnly,
          setIsStatusUpdateDisabled,
        }),
    },
  ];

  const sortedLineItems = invoice?._line_items?.sort(
    (a, b) => a.sequence - b.sequence
  );

  const sortedLineItemsWithTotal = [
    ...(sortedLineItems || []),
    {
      description: 'Total',
      amount: parseFloat(
        (
          sortedLineItems?.reduce((acc, item) => acc + item.amount, 0) || 0
        ).toFixed(2)
      ),
      _paid: {
        total: parseFloat(
          (
            sortedLineItems?.reduce(
              (acc, item) => acc + (item?._paid?.total ?? 0),
              0
            ) || 0
          ).toFixed(2)
        ),
      },
      status: '',
      sequence: (sortedLineItems?.length || 0) + 1,
      style: 'font-semibold',
    },
  ];

  const lineItemForm = useLineItemForm(createLineItem);

  const activeStatement = invoice?.statements?.find(
    (statement) => statement.status === 'active'
  );

  const isRefund = invoice?.subtype === 'REFUND';

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <div className="flex justify-between flex-col md:flex-row md:items-start">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
          Invoice | {invoice?.pixel_invoice_number} {isRefund ? '⏎' : ''}
        </h2>
        <div className="flex gap-5 mb-6 md:mb-0">
          {activeStatement && (
            <button
              onClick={() => downloadStatement(activeStatement, showToast)}
              type="button"
              className="btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
            >
              Statement
            </button>
          )}
          {!isPaid && !readOnly && !isPTypeInvoide && (
            <DeleteButton invoiceId={invoiceId} loanId={loanId} />
          )}
        </div>
      </div>
      {isRefund && (
        <div className="mb-6">
          <Alert message="Refund invoice" />
        </div>
      )}
      <div className="grid gap-5 md:grid-cols-3 xl:grid-cols-4 mb-10">
        {isPaid ? (
          <TextInput label="Date" value={invoiceDate} readOnly />
        ) : (
          <Datepicker
            name="date"
            label="Date"
            value={invoiceDate}
            setFieldValue={(_, value) => setInvoiceDate(value)}
            readOnly={readOnly}
          />
        )}
        <TextInput
          value={
            invoice && moment(invoice.date_overdue).isValid()
              ? moment(invoice.date_overdue).format('MMM DD, YYYY')
              : 'n/a'
          }
          label="Overdue"
          readOnly
        />
        <DatePaid invoice={invoice} />
        {isAllowedToChangeStatus ? (
          <Select
            label="Status"
            options={invoiceStatuses}
            value={invoiceStatus}
            setFieldValue={(_, value) => setInvoiceStatus(value)}
          />
        ) : (
          <TextInput value={invoice?.status} label="Status" readOnly />
        )}
        <CurrencyInput
          value={invoice?.principal_balance}
          label="Principal Balance"
          readOnly
          tooltip="The principal balance before principal is paid on this invoice. This balance is used to calculate interest for this invoice."
          tooltipPosition="bottom"
          tooltipSize="md"
        />
      </div>
      {!readOnly && (
        <div className="mb-10">
          <BillComId
            form={{
              values: {
                billcom_id: invoice?.billcom_id,
              },
            }}
          />
        </div>
      )}
      {loan?.ach_type === 'automatic' && !isPaid ? (
        <div className="flex justify-between flex-col mb-10">
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-2">
            ACH Schedule
          </h2>
          <span className="mb-2">
            This loan is set up for automatic ACH payments.
          </span>
          {!scheduledPaymentsWithKeys.length ? (
            <span>
              There are no ACH payments scheduled for this invoice yet.
            </span>
          ) : null}
          <div className="flex flex-col gap-5">
            {scheduledPaymentsWithKeys.map((payment) => (
              <ScheduledPayment
                key={payment.key}
                payment={payment}
                invoiceId={invoiceId}
                refetch={refetch}
                showToast={showToast}
                isLender={readOnly}
                createOrupdateScheduledPayment={createOrupdateScheduledPayment}
                deleteScheduledPayment={deleteScheduledPayment}
                isSubmitDisabled={isPending || isFetching}
                disabled={isPaid}
                removeEmptyScheduledPayment={removeEmptyScheduledPayment}
              />
            ))}
          </div>
          {!readOnly && (
            <button
              type="button"
              className="btn bg-indigo-500 hover:bg-indigo-600 text-white min-w-[180px] self-start flex justify-center mt-5"
              onClick={addEmptyScheduledPayment}
            >
              <svg
                className="w-4 h-4 fill-current opacity-50 shrink-0"
                viewBox="0 0 16 16"
              >
                <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
              </svg>
              <span className="ml-2 mr-2">Add payment</span>{' '}
              <Tooltip size="lg" svgClassName="text-white">
                You can add a scheduled payment, but doing so will supersede
                automatic ACH scheduling
              </Tooltip>
            </button>
          )}
        </div>
      ) : null}
      <Table
        title="Line items"
        cols={cols}
        rows={sortedLineItemsWithTotal}
        isFetching={isFetching}
        minHeight={400}
        loaderTop={180}
      />

      {isPaid ? (
        <div className="mt-10">
          <Alert message="Paid invoices cannot be modified." />
        </div>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {!readOnly && (
            <FormContainer
              form={lineItemForm}
              groupSizeOffset={222}
              noBillComId
            />
          )}
        </>
      )}
    </div>
  );
}
